import type {
  Branch,
  BranchDailyPoint,
  BranchHeatmap,
  BranchHourOfDayPoint,
  BranchPatch,
  BranchSetup,
  CameraConfig,
  BranchTotals,
  DailyPoint,
  DateRange,
  DayReset,
  DbTable,
  DbTablePage,
  HubLog,
  LogFiles,
  LogRead,
  DaySent,
  DetectorCatalogue,
  AutoFetch,
  Download,
  FfmpegInfo,
  FootageDay,
  FootageRoot,
  GovernorateNode,
  HourOfDayPoint,
  HourlyPoint,
  NvrInfo,
  NvrInput,
  PlaceFilter,
  PersonTrack,
  Planogram,
  PlanogramOut,
  Run,
  RunStatus,
  WorkerRuntimeStatus,
  SalesDailyPoint,
  SalesStatus,
  SalesUpload,
  StaffApplied,
  StaffCreate,
  StaffMark,
  StaffMember,
  StaffPatch,
  Summary,
  TrackList,
  WorkersInfo,
  FleetRow,
  IssuedToken,
  PcStore,
  PcStoreCreate,
  Release,
  ReleaseCreate,
  User,
  VersionInfo,
} from "../types";

const BASE = "/api";

/** Optional bearer token, matching FOOTFALL_API_TOKEN on the server. */
const token = import.meta.env.VITE_API_TOKEN as string | undefined;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Display text for a failed call — the API's detail when there is one. */
export function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : String(err);
}

interface Result<T> {
  data: T;
  status: number;
}

async function send<T>(path: string, init?: RequestInit): Promise<Result<T>> {
  const response = await fetch(`${BASE}${path}`, {
    // Always ask the server: API data is live, and a reply cached by the
    // browser (once, an older server's page for a route it lacked) would
    // outlive any restart or update.
    cache: "no-store",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    // FastAPI reports errors as {detail: string | object}.
    const body = await response.json().catch(() => null);
    const detail = body?.detail;
    throw new ApiError(
      response.status,
      typeof detail === "string" ? detail : response.statusText,
    );
  }
  if (response.status === 204) return { data: undefined as T, status: response.status };
  if (!(response.headers.get("content-type") ?? "").includes("json")) {
    // The console's own page instead of data: the server behind it is an
    // older version than this console (still running after an update).
    throw new ApiError(
      response.status,
      "The server answered with a web page instead of data — it is probably still running the previous version. Restart the El Ezaby Retail Analytics service (or the PC).",
    );
  }
  return { data: (await response.json()) as T, status: response.status };
}

/** The common case: callers that only care about the body. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  return (await send<T>(path, init)).data;
}

async function requestBlob(path: string, init?: RequestInit): Promise<Blob> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
    cache: "no-store",
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      typeof body?.detail === "string" ? body.detail : response.statusText,
    );
  }
  return response.blob();
}

function rangeQuery(
  range: DateRange,
  place: PlaceFilter,
  includeDemo: boolean,
): string {
  const params = new URLSearchParams({
    date_from: range.from,
    date_to: range.to,
  });
  if (place.governorate) params.set("governorate", place.governorate);
  if (place.area) params.set("area", place.area);
  for (const id of place.branchIds) params.append("branch_id", id);
  if (includeDemo) params.set("include_demo", "true");
  return params.toString();
}

export const api = {
  /** What this console is talking to: mode, release, registered branch. */
  version: () => request<VersionInfo>("/version"),

  // --- Branch PC: this PC's stores ------------------------------------------
  pcStores: () => request<PcStore[]>("/pc/stores"),
  /** Adds a store (a hub token, or a name for a local store) and switches to it. */
  addPcStore: (body: PcStoreCreate) =>
    request<PcStore>("/pc/stores", { method: "POST", body: JSON.stringify(body) }),
  activatePcStore: (id: string) =>
    request<PcStore>(`/pc/stores/${encodeURIComponent(id)}/activate`, { method: "POST" }),

  // --- Hub: sign-in (session cookie) ---------------------------------------
  login: (username: string, password: string) =>
    request<User>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  me: () => request<User>("/auth/me"),
  listUsers: () => request<User[]>("/auth/users"),
  createUser: (username: string, password: string) =>
    request<User>("/auth/users", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  deleteUser: (id: number) => request<void>(`/auth/users/${id}`, { method: "DELETE" }),
  changePassword: (current_password: string, new_password: string) =>
    request<void>("/auth/password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    }),

  // --- Hub: fleet and tokens ------------------------------------------------
  fleet: () => request<FleetRow[]>("/fleet"),
  issueToken: (branchId: string) =>
    request<IssuedToken>(`/fleet/${encodeURIComponent(branchId)}/token`, { method: "POST" }),
  revokeToken: (branchId: string) =>
    request<void>(`/fleet/${encodeURIComponent(branchId)}/token`, { method: "DELETE" }),

  // --- Hub: releases ---------------------------------------------------------
  listReleases: () => request<Release[]>("/releases"),
  createRelease: (release: ReleaseCreate) =>
    request<Release>("/releases", { method: "POST", body: JSON.stringify(release) }),
  makeCurrentRelease: (id: number) =>
    request<Release>(`/releases/${id}/current`, { method: "POST" }),
  deleteRelease: (id: number) => request<void>(`/releases/${id}`, { method: "DELETE" }),
  /** A link that redirects to a one-hour download URL. */
  releaseDownloadUrl: (id: number) => `${BASE}/releases/${id}/download`,

  listBranches: () => request<Branch[]>("/branches"),

  listGovernorates: () => request<GovernorateNode[]>("/branches/governorates"),

  createBranch: (
    branch: Omit<Branch, "cameras" | "camera_dirs" | "is_demo"> & {
      cameras?: Record<string, CameraConfig>;
      is_demo?: boolean;
    },
  ) =>
    request<Branch>("/branches", {
      method: "POST",
      body: JSON.stringify(branch),
    }),

  patchBranch: (id: string, patch: BranchPatch) =>
    request<Branch>(`/branches/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Create or replace one entrance camera (id = footage folder = plan marker). */
  putBranchCamera: (id: string, cameraId: string, camera: CameraConfig) =>
    request<Branch>(
      `/branches/${encodeURIComponent(id)}/cameras/${encodeURIComponent(cameraId)}`,
      { method: "PUT", body: JSON.stringify(camera) },
    ),

  /** Remove one entrance camera; recorded aggregates and tracks stay. */
  deleteBranchCamera: (id: string, cameraId: string) =>
    request<Branch>(
      `/branches/${encodeURIComponent(id)}/cameras/${encodeURIComponent(cameraId)}`,
      { method: "DELETE" },
    ),

  /** Link a camera to the folder its recordings already live in
   *  (`<path>/<YYYYMMDD>/*.mp4`, a path on the worker PC). */
  putCameraDir: (id: string, cameraId: string, path: string) =>
    request<Branch>(
      `/branches/${encodeURIComponent(id)}/camera-dirs/${encodeURIComponent(cameraId)}`,
      { method: "PUT", body: JSON.stringify({ path }) },
    ),

  /** Unlink a camera's folder; nothing on disk is touched. */
  deleteCameraDir: (id: string, cameraId: string) =>
    request<Branch>(
      `/branches/${encodeURIComponent(id)}/camera-dirs/${encodeURIComponent(cameraId)}`,
      { method: "DELETE" },
    ),

  deleteBranch: (id: string) =>
    request<void>(`/branches/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /** null = the branch has no floor plan yet. */
  getPlanogram: async (branchId: string): Promise<PlanogramOut | null> => {
    try {
      return await request<PlanogramOut>(
        `/branches/${encodeURIComponent(branchId)}/planogram`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  putPlanogram: (branchId: string, plan: Planogram) =>
    request<PlanogramOut>(`/branches/${encodeURIComponent(branchId)}/planogram`, {
      method: "PUT",
      body: JSON.stringify(plan),
    }),

  deletePlanogram: (branchId: string) =>
    request<void>(`/branches/${encodeURIComponent(branchId)}/planogram`, {
      method: "DELETE",
    }),

  /** The branch's floor plan + camera setup as one portable document. */
  getBranchSetup: (branchId: string) =>
    request<BranchSetup>(`/branches/${encodeURIComponent(branchId)}/setup`),

  /** Replace the branch's plan and cameras with a document's, wholesale.
   *  `force` applies a document exported from a different branch id. */
  putBranchSetup: (branchId: string, setup: BranchSetup, force = false) =>
    request<Branch>(
      `/branches/${encodeURIComponent(branchId)}/setup${force ? "?force=true" : ""}`,
      { method: "PUT", body: JSON.stringify(setup) },
    ),

  listStaff: (branchId: string) =>
    request<StaffMember[]>(`/branches/${encodeURIComponent(branchId)}/staff`),

  getStaffFaceCrop: (branchId: string, staffId: number) =>
    requestBlob(
      `/branches/${encodeURIComponent(branchId)}/staff/${staffId}/face-crop`,
    ),

  patchStaff: (branchId: string, staffId: number, patch: StaffPatch) =>
    request<StaffMember>(
      `/branches/${encodeURIComponent(branchId)}/staff/${staffId}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    ),

  /** Permanently deletes the enrollment and its stored face signature,
   *  unlinks the person's tracks and counts their entries as customers
   *  again; answers with what was unlinked. */
  deleteStaff: (branchId: string, staffId: number) =>
    request<StaffApplied>(
      `/branches/${encodeURIComponent(branchId)}/staff/${staffId}`,
      { method: "DELETE" },
    ),

  listTracks: (
    branchId: string,
    params: { day?: string; cameraId?: string; limit?: number; offset?: number },
  ) => {
    const query = new URLSearchParams({
      limit: String(params.limit ?? 100),
      offset: String(params.offset ?? 0),
    });
    if (params.day) query.set("day", params.day);
    if (params.cameraId) query.set("camera_id", params.cameraId);
    return request<TrackList>(
      `/branches/${encodeURIComponent(branchId)}/tracks?${query.toString()}`,
    );
  },

  getTrackFaceCrop: (
    branchId: string,
    track: Pick<PersonTrack, "run_date" | "camera_id" | "tracker_id">,
  ) =>
    requestBlob(
      `/branches/${encodeURIComponent(branchId)}/tracks/` +
        `${encodeURIComponent(track.run_date)}/` +
        `${encodeURIComponent(track.camera_id)}/` +
        `${track.tracker_id}/face-crop`,
    ),

  /** Enrolls the track's stored face and links every stored track of the
   *  branch that is the same person, on every processed day. */
  markTrackAsStaff: (
    branchId: string,
    track: Pick<PersonTrack, "run_date" | "camera_id" | "tracker_id">,
    payload: StaffCreate,
  ) =>
    request<StaffMark>(
      `/branches/${encodeURIComponent(branchId)}/tracks/` +
        `${encodeURIComponent(track.run_date)}/` +
        `${encodeURIComponent(track.camera_id)}/` +
        `${track.tracker_id}/staff`,
      { method: "POST", body: JSON.stringify(payload) },
    ),

  summary: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<Summary>(`/analytics/summary?${rangeQuery(range, place, includeDemo)}`),

  daily: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<DailyPoint[]>(`/analytics/daily?${rangeQuery(range, place, includeDemo)}`),

  /** `daily` kept apart per branch — one series per compared store. */
  dailyByBranch: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<BranchDailyPoint[]>(
      `/analytics/daily-by-branch?${rangeQuery(range, place, includeDemo)}`,
    ),

  hourly: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<HourlyPoint[]>(`/analytics/hourly?${rangeQuery(range, place, includeDemo)}`),

  hourOfDay: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<HourOfDayPoint[]>(
      `/analytics/hour-of-day?${rangeQuery(range, place, includeDemo)}`,
    ),

  /** `hourOfDay` per branch — one series per compared store. */
  hourOfDayByBranch: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<BranchHourOfDayPoint[]>(
      `/analytics/hour-of-day-by-branch?${rangeQuery(range, place, includeDemo)}`,
    ),

  byBranch: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<BranchTotals[]>(
      `/analytics/by-branch?${rangeQuery(range, place, includeDemo)}`,
    ),

  /** Uploaded POS sales per (branch, day) for the same selection — joined
   *  with `dailyByBranch` in the console into the conversion view. */
  salesByBranch: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<SalesDailyPoint[]>(
      `/analytics/sales-by-branch?${rangeQuery(range, place, includeDemo)}`,
    ),

  salesStatus: (branchId: string) =>
    request<SalesStatus>(`/branches/${encodeURIComponent(branchId)}/sales`),

  /** Attach a POS export (.xlsx or .csv) to the branch; the days it carries
   *  replace what was on file for them. The raw file is the body. */
  uploadSales: (branchId: string, file: File) =>
    request<SalesUpload>(
      `/branches/${encodeURIComponent(branchId)}/sales?${new URLSearchParams({ name: file.name })}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/octet-stream" },
        body: file,
      },
    ),

  /** Remove the branch's sales in an inclusive day range. */
  deleteSales: (branchId: string, range: DateRange) =>
    request<SalesStatus>(
      `/branches/${encodeURIComponent(branchId)}/sales?${new URLSearchParams({
        date_from: range.from,
        date_to: range.to,
      })}`,
      { method: "DELETE" },
    ),

  coverage: (range: DateRange, place: PlaceFilter, includeDemo = false) =>
    request<string[]>(`/analytics/coverage?${rangeQuery(range, place, includeDemo)}`),

  /** Per-camera occupancy over the branch's floor plan; `hours` narrows to a
   *  local hour-of-day band (inclusive). Single-branch by design. */
  heatmap: (
    branchId: string,
    range: DateRange,
    hours?: { from: number; to: number } | null,
  ) => {
    const params = new URLSearchParams({
      branch_id: branchId,
      date_from: range.from,
      date_to: range.to,
    });
    if (hours) {
      params.set("hour_from", String(hours.from));
      params.set("hour_to", String(hours.to));
    }
    return request<BranchHeatmap>(`/analytics/heatmap?${params.toString()}`);
  },

  listRuns: (params: { branchId?: string; status?: RunStatus; limit?: number }) => {
    const query = new URLSearchParams();
    if (params.branchId) query.set("branch_id", params.branchId);
    if (params.status) query.set("status", params.status);
    query.set("limit", String(params.limit ?? 50));
    return request<Run[]>(`/runs?${query.toString()}`);
  },

  /** Detectors installed on the worker machine, default first. */
  detectorModels: () => request<DetectorCatalogue>("/runs/models"),

  /** What the worker machine runs on (device, face backend, decode). */
  workerRuntime: () => request<WorkerRuntimeStatus>("/runs/runtime"),

  /** Ask the worker to probe its runtime again when it is next idle. */
  checkWorkerRuntime: () =>
    request<WorkerRuntimeStatus>("/runs/runtime/check", { method: "POST" }),

  /** `yoloModel` is a detector id from `detectorModels()`; null or "" runs
   *  the machine default. `annotate` (Debug) and `faces` (Demographics):
   *  null leaves them to the machine's defaults. */
  createRun: async (
    branchId: string,
    runDate: string,
    requestedBy: string,
    yoloModel: string | null = null,
    options: { annotate?: boolean | null; faces?: boolean | null } = {},
  ) => {
    const { data, status } = await send<Run>("/runs", {
      method: "POST",
      body: JSON.stringify({
        branch_id: branchId,
        run_date: runDate,
        requested_by: requestedBy,
        yolo_model: yoloModel || null,
        annotate: options.annotate ?? null,
        faces: options.faces ?? null,
      }),
    });
    // 201 means we queued it; 200 means that day was already pending.
    return { run: data, created: status === 201 };
  },

  cancelRun: (id: number) => request<Run>(`/runs/${id}/cancel`, { method: "POST" }),

  databaseTables: () => request<DbTable[]>("/database/tables"),

  databaseTable: (
    table: string,
    opts: { limit: number; offset: number; search: string; sort: string; direction: string },
  ) => {
    const query = new URLSearchParams({
      limit: String(opts.limit),
      offset: String(opts.offset),
      direction: opts.direction,
    });
    if (opts.search) query.set("search", opts.search);
    if (opts.sort) query.set("sort", opts.sort);
    return request<DbTablePage>(`/database/tables/${encodeURIComponent(table)}?${query}`);
  },

  logFiles: () => request<LogFiles>("/logs/files"),

  logFile: (name: string, opts: { level: string; search: string }) => {
    const query = new URLSearchParams();
    if (opts.level) query.set("level", opts.level);
    if (opts.search) query.set("search", opts.search);
    return request<LogRead>(`/logs/files/${encodeURIComponent(name)}?${query}`);
  },

  hubLog: (opts: { limit: number; offset: number; failures: boolean }) =>
    request<HubLog>(
      `/logs/hub?limit=${opts.limit}&offset=${opts.offset}&failures=${opts.failures}`,
    ),

  /** Clear one branch/day's stats (runs are additive and never clear). */
  resetDay: (branchId: string, runDate: string) =>
    request<DayReset>("/runs/reset", {
      method: "POST",
      body: JSON.stringify({ branch_id: branchId, run_date: runDate }),
    }),

  /** Send a run's day to the hub now instead of waiting for the agent's
   *  next sync (branch PC with a hub store only). */
  syncRun: (id: number) => request<DaySent>(`/runs/${id}/sync`, { method: "POST" }),

  footageRoot: () => request<FootageRoot>("/footage/root"),

  /** null reverts to the FOOTFALL_FOOTAGE_ROOT default. */
  setFootageRoot: (path: string | null) =>
    request<FootageRoot>("/footage/root", {
      method: "PUT",
      body: JSON.stringify({ path }),
    }),

  /** Creates the root and one directory per camera branch. */
  createFootageRoot: () =>
    request<FootageRoot>("/footage/root/create", { method: "POST" }),

  footageDay: (branchId: string, day: string) =>
    request<FootageDay>(
      `/footage/day?${new URLSearchParams({ branch_id: branchId, day })}`,
    ),

  /** The store's recorder (per PC); never returns the password. */
  nvr: (branchId: string) =>
    request<NvrInfo>(`/branches/${encodeURIComponent(branchId)}/nvr`),

  saveNvr: (branchId: string, body: NvrInput) =>
    request<NvrInfo>(`/branches/${encodeURIComponent(branchId)}/nvr`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deleteNvr: (branchId: string) =>
    request<NvrInfo>(`/branches/${encodeURIComponent(branchId)}/nvr`, { method: "DELETE" }),

  /** The NVR channel one camera records on; null forgets it. */
  setCameraChannel: (branchId: string, cameraId: string, channel: string | null) =>
    request<NvrInfo>(
      `/branches/${encodeURIComponent(branchId)}/nvr/channels/${encodeURIComponent(cameraId)}`,
      channel === null
        ? { method: "DELETE" }
        : { method: "PUT", body: JSON.stringify({ channel }) },
    ),

  /** Download a day from the NVR; `thenQueue` queues its run on success. */
  startDownload: async (
    branchId: string,
    day: string,
    thenQueue: boolean,
    yoloModel: string | null = null,
    options: { annotate?: boolean | null; faces?: boolean | null } = {},
  ) => {
    const { data, status } = await send<Download>("/downloads", {
      method: "POST",
      body: JSON.stringify({
        branch_id: branchId,
        day,
        then_queue: thenQueue,
        yolo_model: yoloModel || null,
        annotate: options.annotate ?? null,
        faces: options.faces ?? null,
      }),
    });
    return { download: data, created: status === 201 };
  },

  listDownloads: (branchId?: string) =>
    request<Download[]>(
      `/downloads${branchId ? `?${new URLSearchParams({ branch_id: branchId })}` : ""}`,
    ),

  cancelDownload: (id: number) =>
    request<Download>(`/downloads/${id}/cancel`, { method: "POST" }),

  /** The scheduled fetch of unprocessed days from the NVR. */
  autoFetch: () => request<AutoFetch>("/autofetch"),

  setAutoFetch: (enabled: boolean) =>
    request<AutoFetch>("/autofetch", { method: "PUT", body: JSON.stringify({ enabled }) }),

  checkAutoFetchNow: () => request<AutoFetch>("/autofetch/check", { method: "POST" }),

  /** Queue workers on this PC, with nvidia-smi's view of the GPU. */
  workers: () => request<WorkersInfo>("/workers"),

  /** How many workers the desktop app runs; null = the install's default. */
  setWorkers: (count: number | null) =>
    request<WorkersInfo>("/workers", { method: "PUT", body: JSON.stringify({ count }) }),

  /** The ffmpeg the server preprocesses native NVR exports with, if any. */
  footageFfmpeg: () => request<FfmpegInfo>("/footage/ffmpeg"),

  /** A sample clip the browser cannot decode (a native NVR export, or its
   *  first bytes) → the short H.264 MP4 it can. `name` carries the
   *  extension ffmpeg needs. The server keeps nothing. */
  previewFootage: (sample: Blob, name: string) =>
    requestBlob(`/footage/preview?${new URLSearchParams({ name })}`, {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: sample,
    }),
};
